/**
 * Template failure → a sentence the customer can act on.
 *
 * `JsonClient` throws `HttpError`, whose `message` is the REQUEST LINE
 * ("POST /api/templates/ef786a67-…/versions/1/submit -> 422") — the transport, not the
 * cause. Every template surface used to `setError(e.message)`, so the single most damaging
 * moment in the app (pressing Activate on a Draft) printed an internal UUID and an HTTP
 * status into the page and looked like leftover debug output.
 *
 * Modelled on `activation/errors.ts`, which already does this for the login path. The
 * cloud's shapes we match on (`cloud/app/catalog/router.py`):
 *   - 422 `{"detail": {"errors": [...], "views": {"<view_type>": [...]}}}` — geometry
 *     validation; `views` names the editor TAB to fix.
 *   - 409 `{"detail": "Stale version_token; reload"}` / "Locked version is immutable".
 *   - 401 / 403 — signed out, or not this customer's template.
 * A raw request line is never returned, whatever the failure.
 */

/** `validate_masks` error codes (cloud `catalog/geometry.py`) → what they mean on screen. */
const GEOMETRY_REASON: Record<string, string> = {
  empty_area: "has no print area",
  out_of_bounds: "has a print area outside the case",
  self_intersect: "has a print area that crosses itself",
  cutout_not_subtracted: "has a camera cutout that misses the print area",
};

/** The `detail` payload FastAPI puts on an HTTPException (string, object, or its own
 *  validation-error LIST — which is developer-facing and must never be shown). */
function detailOf(error: unknown): unknown {
  const body = (error as { body?: unknown } | null)?.body;
  return (body as { detail?: unknown } | null)?.detail;
}

function statusOf(error: unknown): number | undefined {
  const status = (error as { status?: unknown } | null)?.status;
  return typeof status === "number" ? status : undefined;
}

/** A fetch that never reached the server (no HTTP status, a transport-level message). */
function isNetworkFailure(error: unknown): boolean {
  if (statusOf(error) !== undefined) return false;
  const msg = error instanceof Error ? error.message : String(error);
  return /failed to fetch|networkerror|network request failed|load failed|err_(internet|network|connection)/i.test(msg);
}

/** "front" → "Front". View types are slugs; the editor tab shows them title-cased. */
function viewName(view: string): string {
  return view.charAt(0).toUpperCase() + view.slice(1).replace(/[-_]+/g, " ");
}

/** The per-view geometry sentence(s) for a 422, or "" when this isn't that shape. */
function geometryMessage(detail: unknown): string {
  const views = (detail as { views?: unknown } | null)?.views;
  if (!views || typeof views !== "object" || Array.isArray(views)) return "";
  const entries = Object.entries(views as Record<string, unknown>);
  if (entries.length === 0) return "";
  const clauses = entries.map(([view, codes]) => {
    const first = Array.isArray(codes) ? String(codes[0] ?? "") : "";
    return `${viewName(view)} view ${GEOMETRY_REASON[first] ?? "has geometry the cloud rejected"}`;
  });
  return `${clauses.join("; ")} — open the template and draw or remove that view.`;
}

/**
 * A human sentence for a template failure.
 *
 * `action` is a lowercase verb phrase naming what was being attempted, e.g.
 * "activate this template" / "load the templates" — it is used both for the permission
 * message and for the last-resort fallback, so the operator always knows WHICH action
 * failed even when the cause is unrecognised.
 */
export function templateErrorMessage(error: unknown, action: string): string {
  if (isNetworkFailure(error)) return "Can't reach the cloud — check the connection.";

  const status = statusOf(error);
  const detail = detailOf(error);

  if (status === 401 || status === 403) return `You don't have permission to ${action}.`;
  if (status === 409) return "Someone else changed this template — refresh and try again.";

  if (status === 422) {
    const geometry = geometryMessage(detail);
    if (geometry) return geometry;
  }
  // A plain-string `detail` is the cloud's own operator-facing sentence — prefer it.
  // A LIST detail is FastAPI's schema-validation dump (`{"loc": [...], "msg": "Field
  // required"}`): developer output, so it falls through to the generic sentence below.
  if (typeof detail === "string" && detail.trim()) return detail.trim();

  if (status) return `Couldn't ${action} (${status}).`;

  // No HTTP status → this is a LOCAL failure (e.g. "has no saved geometry"), not a
  // transport one. Its own message is the useful, already-human one and is never a request
  // line, so it is passed through rather than replaced with a generic sentence.
  const raw = error instanceof Error ? error.message.trim() : String(error ?? "").trim();
  return raw || `Couldn't ${action}.`;
}
